import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Search, FileText, Users, Building2, Trophy, Mail, Briefcase,
  ChevronRight, ChevronLeft, X, IndianRupee, Sparkles,
  Plus, Lock, Phone, ShieldCheck, Loader2, KeyRound, Send,
  Filter, Calendar, ArrowUpDown, Award, MessageSquareText,
} from 'lucide-react';
import JDModal from '../components/JDModal';

const customStyles = `
  @keyframes blob {
    0% { transform: translate(0px, 0px) scale(1); }
    33% { transform: translate(30px, -50px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.9); }
    100% { transform: translate(0px, 0px) scale(1); }
  }
  .animate-blob { animation: blob 15s infinite alternate ease-in-out; }
`;

// ============================================================
// MOCK DATA — placeholder only, will be replaced by API calls
// once the mail-extraction pipeline (JD + resume + candidate/
// vendor/staffing/CTC parsing) is wired up on the backend.
// ============================================================

const MOCK_JDS = [
  {
    id: 'jd-1',
    title: 'Senior React Developer',
    skills: 'React, TypeScript, Node.js',
    staffingCompany: 'ProStaff Inc',
    openPositions: 3,
    createdAt: '2026-09-01',
  },
  {
    id: 'jd-2',
    title: 'Data Engineer (AWS)',
    skills: 'Python, Spark, AWS, Airflow',
    staffingCompany: 'ProStaff Inc',
    openPositions: 2,
    createdAt: '2026-09-05',
  },
  {
    id: 'jd-3',
    title: 'DevOps Engineer',
    skills: 'Kubernetes, Terraform, CI/CD',
    staffingCompany: 'NextGen Talent',
    openPositions: 1,
    createdAt: '2026-09-10',
  },
];

const MOCK_RESUMES = [
  { id: 'res-1', candidateName: 'Aisha Khan', fileName: 'Aisha_Khan_Resume.pdf', skills: 'React, Redux, TypeScript', experience: '5 yrs', uploadedAt: '2026-09-08' },
  { id: 'res-2', candidateName: 'Rohit Sharma', fileName: 'Rohit_Sharma_CV.pdf', skills: 'Python, Spark, AWS', experience: '4 yrs', uploadedAt: '2026-09-09' },
  { id: 'res-3', candidateName: 'Meera Iyer', fileName: 'Meera_Iyer_Resume.pdf', skills: 'Kubernetes, Terraform, Go', experience: '6 yrs', uploadedAt: '2026-09-11' },
  { id: 'res-4', candidateName: 'Daniel Fernandes', fileName: 'Daniel_F_Resume.pdf', skills: 'React, Next.js, GraphQL', experience: '3 yrs', uploadedAt: '2026-09-12' },
];

const MOCK_SUBMISSIONS = [
  {
    id: 'sub-1',
    candidateName: 'Aisha Khan',
    candidateEmail: 'aisha.khan@example.com',
    vendorName: 'TalentBridge Solutions',
    staffingCompany: 'ProStaff Inc',
    jdId: 'jd-1',
    jdTitle: 'Senior React Developer',
    resumeId: 'res-1',
    marketedCtc: '12 LPA',
    expectedCtc: '13.5 LPA',
    candidatePhone: '+91 98111 22334',
    mailSubject: 'Submission - Aisha Khan for Sr. React Developer',
    mailReceivedAt: '2026-09-12 10:14 AM',
    senderEmail: 'priya@talentbridge.com',
    matchedVendorId: 'ven-1',
    classification: 'Candidate Submission',
    forwardedToInbox: 'ingest@recruitguru.ai',
    status: 'New',
    atsScore: 91,
    pipelineStage: 'invite_sent',
    rounds: [
      { round: 1, title: 'Screening Call', summary: 'Strong communication, confident on React fundamentals and state management.', suggestion: 'Move to technical round.', status: 'Cleared' },
    ],
  },
  {
    id: 'sub-2',
    candidateName: 'Rohit Sharma',
    candidateEmail: 'rohit.sharma@example.com',
    vendorName: 'Apex Workforce',
    staffingCompany: 'ProStaff Inc',
    jdId: 'jd-2',
    jdTitle: 'Data Engineer (AWS)',
    resumeId: 'res-2',
    marketedCtc: '11 LPA',
    expectedCtc: '12 LPA',
    candidatePhone: '+91 90333 44556',
    mailSubject: 'Profile Submission - Rohit Sharma / Data Engineer',
    mailReceivedAt: '2026-09-13 03:47 PM',
    senderEmail: 'karan@apexworkforce.com',
    matchedVendorId: 'ven-2',
    classification: 'Candidate Submission',
    forwardedToInbox: 'ingest@recruitguru.ai',
    status: 'Reviewed',
    atsScore: 86,
    pipelineStage: 'interview',
    rounds: [
      { round: 1, title: 'Screening Call', summary: 'Good grasp of AWS data pipelines, clear on past project ownership.', suggestion: 'Proceed to technical deep-dive.', status: 'Cleared' },
      { round: 2, title: 'Technical Round', summary: 'Solid on Spark internals, needs more depth on Airflow DAG design.', suggestion: 'Borderline — recommend one more round.', status: 'In Progress' },
    ],
  },
  {
    id: 'sub-3',
    candidateName: 'Meera Iyer',
    candidateEmail: 'meera.iyer@example.com',
    vendorName: 'TalentBridge Solutions',
    staffingCompany: 'NextGen Talent',
    jdId: 'jd-3',
    jdTitle: 'DevOps Engineer',
    resumeId: 'res-3',
    marketedCtc: '15 LPA',
    expectedCtc: '15 LPA',
    candidatePhone: '+91 97222 88990',
    mailSubject: 'Candidate Profile - Meera Iyer for DevOps role',
    mailReceivedAt: '2026-09-14 09:02 AM',
    senderEmail: 'priya@talentbridge.com',
    matchedVendorId: 'ven-1',
    classification: 'Candidate Submission',
    forwardedToInbox: 'ingest@recruitguru.ai',
    status: 'New',
    atsScore: 95,
    pipelineStage: 'resume_received',
    rounds: [],
  },
  {
    id: 'sub-4',
    candidateName: 'Daniel Fernandes',
    candidateEmail: 'daniel.f@example.com',
    vendorName: 'Apex Workforce',
    staffingCompany: 'ProStaff Inc',
    jdId: 'jd-1',
    jdTitle: 'Senior React Developer',
    resumeId: 'res-4',
    marketedCtc: '9 LPA',
    expectedCtc: '10 LPA',
    candidatePhone: '+91 96444 11778',
    mailSubject: 'Resume Submission - Daniel Fernandes',
    mailReceivedAt: '2026-09-11 06:30 PM',
    senderEmail: 'karan@apexworkforce.com',
    matchedVendorId: 'ven-2',
    classification: 'Candidate Submission',
    forwardedToInbox: 'ingest@recruitguru.ai',
    status: 'Shortlisted',
    atsScore: 74,
    pipelineStage: 'waiting',
    rounds: [
      { round: 1, title: 'Screening Call', summary: 'Confident on React/Next.js, good portfolio of shipped projects.', suggestion: 'Move to technical round.', status: 'Cleared' },
      { round: 2, title: 'Technical Round', summary: 'Clean code walkthrough, handled system design questions well.', suggestion: 'Strong hire signal — proceed to final round.', status: 'Cleared' },
      { round: 3, title: 'Final / Hiring Manager', summary: 'Awaiting feedback from hiring manager after final round.', suggestion: 'Follow up in 2 business days.', status: 'In Progress' },
    ],
  },
];

const MOCK_VENDORS = [
  {
    id: 'ven-1',
    companyName: 'TalentBridge Solutions',
    contactName: 'Priya Nair',
    email: 'priya@talentbridge.com',
    phone: '+91 98765 43210',
    staffingCompany: 'ProStaff Inc',
    accessLevel: 'Standard',
    status: 'Active',
    createdAt: '2026-08-20',
  },
  {
    id: 'ven-2',
    companyName: 'Apex Workforce',
    contactName: 'Karan Mehta',
    email: 'karan@apexworkforce.com',
    phone: '+91 90000 11223',
    staffingCompany: 'ProStaff Inc',
    accessLevel: 'Standard',
    status: 'Active',
    createdAt: '2026-08-25',
  },
  {
    id: 'ven-3',
    companyName: 'NorthStar Recruiters',
    contactName: 'Leah Thomas',
    email: 'leah@northstarrecruiters.com',
    phone: '+91 99887 66554',
    staffingCompany: 'NextGen Talent',
    accessLevel: 'Restricted',
    status: 'Pending',
    createdAt: '2026-09-10',
  },
];

const ACCESS_LEVELS = ['Standard', 'Restricted', 'Admin'];

const VENDOR_STATUS_STYLES = {
  Active: 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/20',
  Pending: 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-500/20',
  Suspended: 'bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-500/20',
};

const STATUS_STYLES = {
  New: 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-500/20',
  Reviewed: 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-500/20',
  Shortlisted: 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/20',
};

// Candidate pipeline stages shown as a horizontal stepper in the View
// Summary modal — each submission's pipelineStage marks how far along it
// has progressed through this fixed sequence.
const PIPELINE_STAGES = [
  { key: 'jd_shared', label: 'JD Shared' },
  { key: 'resume_received', label: 'Resume Received' },
  { key: 'invite_sent', label: 'Invite Sent' },
  { key: 'interview', label: 'Interview' },
  { key: 'waiting', label: 'Waiting' },
];

const ROUND_STATUS_STYLES = {
  Cleared: 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/20',
  'In Progress': 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-500/20',
  Rejected: 'bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-500/20',
};

const TABS = [
  { id: 'jds', label: 'Job Requirement', icon: FileText },
  { id: 'resumes', label: 'Resumes', icon: Users },
  { id: 'submissions', label: 'Vendor Submissions', icon: Building2 },
  { id: 'vendors', label: 'Vendor Accounts', icon: KeyRound },
];

const AmbientGlows = () => (
  <>
    <div className="absolute top-[-10%] left-[10%] w-[50%] h-[50%] rounded-full bg-indigo-200/40 dark:bg-indigo-600/10 blur-[120px] animate-blob z-0 pointer-events-none fixed"></div>
    <div className="absolute bottom-[10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-violet-200/40 dark:bg-violet-600/10 blur-[120px] animate-blob z-0 pointer-events-none fixed" style={{ animationDelay: '2s' }}></div>
  </>
);

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md p-8 flex flex-col justify-between hover:bg-slate-50 dark:hover:bg-[#111111]/80 transition-colors">
    <div className="flex items-center gap-3 mb-8">
      <div className={accent}><Icon size={18} strokeWidth={1.5} /></div>
      <span className="text-sm font-semibold tracking-wide text-slate-500 dark:text-slate-400">{label}</span>
    </div>
    <span className="text-5xl font-light tracking-tighter text-slate-900 dark:text-white">{value}</span>
  </div>
);

const SearchBar = ({ value, onChange, placeholder }) => (
  <div className="relative w-full sm:w-72 group">
    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 group-focus-within:text-indigo-500 dark:group-focus-within:text-indigo-400 transition-colors" size={16} />
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full pl-11 pr-4 py-3 bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-2xl text-sm placeholder:text-[12px] text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 transition-all shadow-sm dark:shadow-none"
    />
  </div>
);

// Compact <select> dropdown used for status/vendor/staffing-company filters —
// mirrors SearchBar's input styling (bg-white dark:bg-black/50, rounded-2xl).
const FilterSelect = ({ value, onChange, options, icon: Icon = Filter, allLabel = 'All' }) => (
  <div className="relative w-full sm:w-48 group">
    <Icon className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 group-focus-within:text-indigo-500 dark:group-focus-within:text-indigo-400 transition-colors pointer-events-none" size={15} />
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full pl-11 pr-4 py-3 bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-2xl text-sm text-slate-900 dark:text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 transition-all shadow-sm dark:shadow-none appearance-none cursor-pointer"
    >
      <option value="">{allLabel}</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  </div>
);

// Multi-select checkbox dropdown, used for Vendor filters specifically
// (the only filter that needs picking more than one option at once).
// Same trigger styling as FilterSelect; opens a checkbox list panel below.
const MultiSelectFilter = ({ values, onChange, options, icon: Icon = Filter, allLabel = 'All' }) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (opt) => {
    onChange(values.includes(opt) ? values.filter(v => v !== opt) : [...values, opt]);
  };

  const label = values.length === 0
    ? allLabel
    : values.length === 1
      ? values[0]
      : `${values.length} vendors selected`;

  return (
    <div className="relative w-full sm:w-48" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center gap-2 pl-11 pr-4 py-3 bg-white dark:bg-black/50 border rounded-2xl text-sm text-left truncate transition-all shadow-sm dark:shadow-none relative ${
          open ? 'border-indigo-500 ring-1 ring-indigo-500/50' : 'border-slate-200 dark:border-white/10'
        } ${values.length > 0 ? 'text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-400'}`}
      >
        <Icon className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 pointer-events-none" size={15} />
        <span className="truncate">{label}</span>
      </button>

      {open && (
        <div className="absolute z-20 mt-2 w-full min-w-[200px] bg-white dark:bg-[#111] border border-slate-200 dark:border-white/10 rounded-2xl shadow-lg overflow-hidden py-1 max-h-64 overflow-y-auto custom-scrollbar">
          <button
            type="button"
            onClick={() => onChange([])}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
          >
            <input type="checkbox" readOnly checked={values.length === 0} className="w-4 h-4 rounded accent-indigo-600 pointer-events-none" />
            <span className="text-slate-700 dark:text-slate-200 font-semibold">{allLabel}</span>
          </button>
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => toggleOption(opt)}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-left hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
            >
              <input type="checkbox" readOnly checked={values.includes(opt)} className="w-4 h-4 rounded accent-indigo-600 pointer-events-none" />
              <span className="text-slate-700 dark:text-slate-200 truncate">{opt}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// From/to date-range pair used for createdAt/uploadedAt/mailReceivedAt
// filters — plain <input type="date">, no external date-picker library.
const DateRangeFilter = ({ from, to, onFromChange, onToChange }) => (
  <div className="flex items-center gap-2 w-full sm:w-auto">
    <div className="relative flex-1 sm:flex-none group">
      <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 group-focus-within:text-indigo-500 dark:group-focus-within:text-indigo-400 transition-colors pointer-events-none" size={14} />
      <input
        type="date"
        value={from}
        onChange={(e) => onFromChange(e.target.value)}
        className="w-full sm:w-40 pl-10 pr-3 py-3 bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-2xl text-[13px] text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 transition-all shadow-sm dark:shadow-none"
      />
    </div>
    <span className="text-slate-400 dark:text-slate-500 text-xs font-semibold">to</span>
    <input
      type="date"
      value={to}
      onChange={(e) => onToChange(e.target.value)}
      className="flex-1 sm:flex-none sm:w-40 px-4 py-3 bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-2xl text-[13px] text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50 transition-all shadow-sm dark:shadow-none"
    />
  </div>
);

// Toggle button used to flip ATS-score sort direction on the resume
// ranking tables (JDResumesView / RankResumesView).
const SortToggle = ({ direction, onToggle, label = 'ATS Score' }) => (
  <button
    type="button"
    onClick={onToggle}
    className="shrink-0 flex items-center gap-2 px-5 py-3 bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-2xl text-sm text-slate-600 dark:text-slate-300 hover:border-indigo-300 dark:hover:border-indigo-500/40 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all shadow-sm dark:shadow-none"
    title="Toggle sort direction"
  >
    <ArrowUpDown size={14} />
    {label}: {direction === 'desc' ? 'High to Low' : 'Low to High'}
  </button>
);

const EmptyState = ({ icon: Icon, title, subtitle }) => (
  <div className="text-center py-32 bg-white dark:bg-white/5 backdrop-blur-sm rounded-[2rem] border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none">
    <div className="w-16 h-16 bg-slate-50 dark:bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6 border border-slate-100 dark:border-white/10">
      <Icon size={24} className="text-slate-400" strokeWidth={1.5} />
    </div>
    <h3 className="text-lg font-medium tracking-wide text-slate-900 dark:text-white mb-2">{title}</h3>
    <p className="text-slate-500 text-xs tracking-wide">{subtitle}</p>
  </div>
);

// ------------------------------------------------------------
// JD DETAIL VIEW — opened from a Job Requirement card.
// JD creation + vendor assignment is its own self-contained flow: JD ->
// Assign Vendors. What happens after a vendor is assigned (resumes coming
// in, ranking, inviting candidates) is a separate concern already covered
// by the Vendor Submissions tab.
const JD_FLOW_STAGES = [
  { key: 'jd', label: 'JD', icon: FileText },
  { key: 'assign', label: 'Assign Vendors', icon: Send },
];

const JDDetailView = ({ jd, onBack, onShared, initialStage = 'jd', onStageChange }) => {
  // Stage is fully driven by the URL (initialStage, derived from the route
  // path by the caller) — no local view-state, so browser back/forward and
  // a hard refresh both land on the right stage.
  const activeStage = initialStage;
  const [checkedVendorIds, setCheckedVendorIds] = useState({});
  const [sharedVendorIds, setSharedVendorIds] = useState([]);
  const [isSharing, setIsSharing] = useState(false);

  // Vendors scoped to this JD's staffing company only — never all vendors.
  const scopedVendors = useMemo(() => {
    if (!jd) return [];
    return MOCK_VENDORS.filter(v => v.staffingCompany === jd.staffingCompany).map(v => {
      const vendorSubmissions = MOCK_SUBMISSIONS.filter(s => s.matchedVendorId === v.id);
      return {
        ...v,
        totalResumes: vendorSubmissions.length,
        totalHires: vendorSubmissions.filter(s => s.status === 'Shortlisted').length,
      };
    });
  }, [jd]);

  const toggleVendorChecked = (vendorId) => {
    setCheckedVendorIds(prev => ({ ...prev, [vendorId]: !prev[vendorId] }));
  };

  // Mock share — once wired to the backend this becomes an API call that
  // marks the JD visible only to the checked vendors.
  const handleShareJD = () => {
    const ids = Object.keys(checkedVendorIds).filter(id => checkedVendorIds[id]);
    if (ids.length === 0) return;
    setIsSharing(true);
    setTimeout(() => {
      setSharedVendorIds(prev => Array.from(new Set([...prev, ...ids])));
      setIsSharing(false);
      onShared?.(ids);
    }, 500);
  };

  const stageIndex = JD_FLOW_STAGES.findIndex(s => s.key === activeStage);

  return (
    <div>
      <button
        onClick={onBack}
        className="flex items-center text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium mb-8 transition-colors w-max text-sm tracking-wide"
      >
        <ChevronLeft size={18} /> Back to Resumes
      </button>

      {/* Flow stepper — JD -> Assign Vendors */}
      <div className="bg-white dark:bg-white/5 backdrop-blur-md rounded-[2rem] border border-slate-200 dark:border-white/5 shadow-sm dark:shadow-none mb-8 p-6">
        <div className="flex items-center w-full">
          {JD_FLOW_STAGES.map((stage, i) => {
            const Icon = stage.icon;
            const isDone = i < stageIndex;
            const isActive = i === stageIndex;
            return (
              <React.Fragment key={stage.key}>
                <button
                  onClick={() => onStageChange?.(stage.key)}
                  className="flex flex-col items-center gap-2 shrink-0 group"
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                    isActive
                      ? 'bg-indigo-600 border-indigo-600 shadow-[0_0_15px_rgba(99,102,241,0.4)] text-white'
                      : isDone
                        ? 'bg-emerald-500 border-emerald-500 text-white'
                        : 'bg-transparent border-slate-300 dark:border-slate-600 text-slate-400 dark:text-slate-500 group-hover:border-slate-400 dark:group-hover:border-slate-400'
                  }`}>
                    <Icon size={16} strokeWidth={2} />
                  </div>
                  <span className={`text-[11px] font-bold tracking-wide whitespace-nowrap ${
                    isActive ? 'text-slate-900 dark:text-white' : isDone ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'
                  }`}>
                    {stage.label}
                  </span>
                </button>
                {i < JD_FLOW_STAGES.length - 1 && (
                  <div className={`flex-1 h-[2px] mx-4 mb-5 ${i < stageIndex ? 'bg-emerald-500' : 'bg-slate-200 dark:bg-slate-700'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* STAGE 1: JD */}
      {activeStage === 'jd' && (
        <div className="bg-white dark:bg-white/5 backdrop-blur-md p-8 rounded-[2rem] border border-slate-200 dark:border-white/5 shadow-sm dark:shadow-none mb-8">
          <p className="text-[12px] font-semibold text-slate-500 dark:text-slate-400 tracking-wide mb-2 flex items-center gap-1.5">
            <FileText size={12} /> Job Requirement
          </p>
          <h2 className="text-2xl font-semibold tracking-wide text-slate-900 dark:text-white mb-3">{jd?.title}</h2>
          <p className="text-[12px] text-slate-500 dark:text-slate-400 tracking-wide font-semibold mb-4 flex items-center gap-1.5">
            <Building2 size={12} /> {jd?.staffingCompany} · {jd?.openPositions} open position{jd?.openPositions !== 1 ? 's' : ''}
          </p>
          <div className="flex items-center gap-2 flex-wrap mb-8">
            {jd?.skills.split(',').map((s, i) => (
              <span key={i} className="px-2.5 py-1 bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-300 text-[12px] rounded-md font-bold tracking-wide border border-slate-200 dark:border-white/10 whitespace-nowrap">
                {s.trim()}
              </span>
            ))}
          </div>
          <button
            onClick={() => onStageChange?.('assign')}
            className="bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-500 text-white px-8 py-3 rounded-2xl text-xs font-bold tracking-wide flex items-center justify-center gap-2 shadow-md dark:shadow-[0_0_15px_rgba(99,102,241,0.3)] transition-all active:scale-95"
          >
            Next: Assign Vendors <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* STAGE 2: ASSIGN VENDORS */}
      {activeStage === 'assign' && (
        <div className="bg-white dark:bg-white/5 backdrop-blur-md rounded-[2rem] border border-slate-200 dark:border-white/5 shadow-sm dark:shadow-none mb-8 p-6">
          <h3 className="text-lg font-semibold tracking-wide text-slate-900 dark:text-white mb-1 flex items-center gap-2">
            <Send size={16} className="text-indigo-500 dark:text-indigo-400" /> Assign Vendors
          </h3>
          <p className="text-[12px] text-slate-500 dark:text-slate-400 tracking-wide font-semibold mb-6">Pick which vendors of {jd?.staffingCompany} get to market this JD.</p>

          {scopedVendors.length === 0 ? (
            <p className="text-[12px] text-amber-600 dark:text-amber-400 font-semibold tracking-wide py-6">No vendors created for {jd?.staffingCompany} yet — create one in Vendor Accounts first.</p>
          ) : (
            <>
              <div className="rounded-2xl border border-slate-200 dark:border-white/10 overflow-hidden">
                <table className="w-full text-left">
                  <thead className="bg-slate-50/50 dark:bg-white/5 text-[13px] text-slate-500 dark:text-slate-400 tracking-wide border-b border-slate-200 dark:border-white/10">
                    <tr>
                      <th className="px-5 py-4 font-semibold w-10"></th>
                      <th className="px-5 py-4 font-semibold">Vendor Name</th>
                      <th className="px-5 py-4 font-semibold text-center">Total Resumes</th>
                      <th className="px-5 py-4 font-semibold text-center">Total Hires</th>
                      <th className="px-5 py-4 font-semibold text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                    {scopedVendors.map((v) => {
                      const isShared = sharedVendorIds.includes(v.id);
                      return (
                        <tr key={v.id} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                          <td className="px-5 py-4">
                            <input
                              type="checkbox"
                              checked={!!checkedVendorIds[v.id]}
                              onChange={() => toggleVendorChecked(v.id)}
                              className="w-4 h-4 rounded accent-indigo-600 cursor-pointer"
                            />
                          </td>
                          <td className="px-5 py-4 font-medium text-slate-900 dark:text-white text-sm">{v.companyName}</td>
                          <td className="px-5 py-4 text-center text-slate-600 dark:text-slate-300 text-sm">{v.totalResumes}</td>
                          <td className="px-5 py-4 text-center text-slate-600 dark:text-slate-300 text-sm">{v.totalHires}</td>
                          <td className="px-5 py-4 text-right">
                            {isShared ? (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-[12px] rounded-md font-bold tracking-wide border border-emerald-200 dark:border-emerald-500/20">
                                <ShieldCheck size={12} /> Shared
                              </span>
                            ) : (
                              <span className="text-[12px] text-slate-400 dark:text-slate-500 font-semibold tracking-wide">Not shared</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end pt-6">
                <button
                  onClick={handleShareJD}
                  disabled={isSharing || Object.values(checkedVendorIds).every(v => !v)}
                  className="bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-500 text-white px-8 py-3 rounded-2xl text-xs font-bold tracking-wide flex items-center justify-center gap-2 shadow-md dark:shadow-[0_0_15px_rgba(99,102,241,0.3)] transition-all active:scale-95 disabled:opacity-50"
                >
                  {isSharing ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  {isSharing ? 'Sharing...' : 'Share JD'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const RESUME_INVITE_STATUS_STYLES = {
  'Not Sent': 'bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-white/10',
  'Invite Sent': 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-500/20',
  'Hired': 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/20',
};

// Contact Vendor — compose and send a message to the vendor in-app,
// instead of handing off to the OS mail client via a mailto: link.
const ContactVendorModal = ({ vendor, candidateName, onClose }) => {
  const [message, setMessage] = useState(
    candidateName ? `Hi ${vendor?.contactName || ''},\n\nRegarding ${candidateName} — ` : ''
  );
  const [isSending, setIsSending] = useState(false);
  const [sent, setSent] = useState(false);

  // Mock send — once wired to the backend this becomes an API call that
  // actually delivers the message to the vendor's registered email.
  const handleSend = () => {
    if (!message.trim() || isSending) return;
    setIsSending(true);
    setTimeout(() => {
      setIsSending(false);
      setSent(true);
    }, 600);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex justify-center z-[60] px-4 pt-28 pb-10 overflow-y-auto">
      <div className="bg-[#0B0F19] rounded-[2rem] shadow-2xl w-full max-w-lg overflow-hidden flex flex-col relative border border-white/10 max-h-full self-start my-auto">
        <div className="px-8 pt-8 pb-5 flex justify-between items-start shrink-0 border-b border-white/5">
          <div>
            <p className="text-[12px] font-semibold text-slate-500 tracking-wide mb-1">Contact Vendor</p>
            <h2 className="text-xl font-semibold text-white tracking-wide">{vendor?.companyName || 'Vendor'}</h2>
            <p className="text-slate-500 text-[12px] tracking-wide font-mono mt-1">{vendor?.email}</p>
          </div>
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center bg-white/5 rounded-2xl text-slate-400 hover:text-white hover:bg-white/10 transition-all border border-white/10 shrink-0">
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        <div className="px-8 py-6">
          {sent ? (
            <div className="flex flex-col items-center text-center py-8">
              <div className="w-14 h-14 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-full flex items-center justify-center mb-5">
                <ShieldCheck size={24} strokeWidth={1.5} />
              </div>
              <p className="text-white text-sm font-semibold mb-1">Message sent</p>
              <p className="text-slate-500 text-[12px] tracking-wide">{vendor?.companyName} will receive this at {vendor?.email}</p>
            </div>
          ) : (
            <>
              <label className="block text-[12px] font-semibold text-slate-500 tracking-wide mb-3">Message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={isSending}
                placeholder="Write your message to the vendor..."
                className="w-full bg-black/50 border border-white/10 rounded-2xl px-5 py-4 text-white font-medium text-sm placeholder:text-[12px] focus:outline-none focus:border-indigo-500 transition-all min-h-[160px] resize-none leading-relaxed custom-scrollbar placeholder-slate-600 disabled:opacity-60"
              />
              <div className="flex justify-end pt-6">
                <button
                  onClick={handleSend}
                  disabled={isSending || !message.trim()}
                  className="bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-500 text-white px-8 py-3 rounded-2xl text-xs font-bold tracking-wide flex items-center justify-center gap-2 shadow-md dark:shadow-[0_0_15px_rgba(99,102,241,0.3)] transition-all active:scale-95 disabled:opacity-50"
                >
                  {isSending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  {isSending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Separate full-page view opened after "Share JD" — the resumes vendors
// send back for this JD, ranked, with per-candidate invite status.
const JDResumesView = ({ jd, onBack, onShareJD, onRankResumes }) => {
  const [inviteStatus, setInviteStatus] = useState({});
  const [invitingIds, setInvitingIds] = useState({});
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [contactRow, setContactRow] = useState(null);
  const [nameQuery, setNameQuery] = useState('');
  const [vendorFilters, setVendorFilters] = useState([]);
  const [sortDir, setSortDir] = useState('desc');

  const allRows = useMemo(() => {
    if (!jd) return [];
    return MOCK_SUBMISSIONS
      .filter(s => s.jdId === jd.id)
      .map(s => {
        const resume = MOCK_RESUMES.find(r => r.id === s.resumeId);
        return { ...s, experience: resume?.experience || '—' };
      });
  }, [jd]);

  const vendorOptions = useMemo(() => (
    [...new Set(allRows.map(r => r.vendorName))].sort()
  ), [allRows]);

  const rows = useMemo(() => {
    const q = nameQuery.trim().toLowerCase();
    return allRows
      .filter(r => (!q || r.candidateName.toLowerCase().includes(q)))
      .filter(r => (vendorFilters.length === 0 || vendorFilters.includes(r.vendorName)))
      .sort((a, b) => sortDir === 'desc'
        ? (b.atsScore ?? -1) - (a.atsScore ?? -1)
        : (a.atsScore ?? -1) - (b.atsScore ?? -1));
  }, [allRows, nameQuery, vendorFilters, sortDir]);

  const getStatus = (submissionId) => inviteStatus[submissionId] || (
    MOCK_SUBMISSIONS.find(s => s.id === submissionId)?.status === 'Shortlisted' ? 'Invite Sent' : 'Not Sent'
  );

  // Reuses the existing campaign invite flow (POST /campaigns/, then
  // POST /campaigns/{id}/launch) already driven by Schedule.js.
  const handleSendInvitation = (submissionId) => {
    setInvitingIds(prev => ({ ...prev, [submissionId]: true }));
    setTimeout(() => {
      setInviteStatus(prev => ({ ...prev, [submissionId]: 'Invite Sent' }));
      setInvitingIds(prev => ({ ...prev, [submissionId]: false }));
    }, 500);
  };

  return (
    <div>
      <button
        onClick={onBack}
        className="flex items-center text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium mb-8 transition-colors w-max text-sm tracking-wide"
      >
        <ChevronLeft size={18} /> Back to Job Requirement
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h3 className="text-lg font-semibold tracking-wide text-slate-900 dark:text-white mb-1 flex items-center gap-2">
            <Users size={16} className="text-indigo-500 dark:text-indigo-400" /> Resumes
          </h3>
          <p className="text-[12px] text-slate-500 dark:text-slate-400 tracking-wide font-semibold">Resumes received from assigned vendors, ranked by ATS score.</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={onRankResumes}
            className="bg-white dark:bg-white/5 text-indigo-600 dark:text-indigo-400 border border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/10 px-8 py-3 rounded-2xl text-xs font-bold tracking-wide flex items-center justify-center gap-2 transition-all active:scale-95 whitespace-nowrap"
          >
            <Trophy size={16} /> Rank Resumes
          </button>
          <button
            onClick={onShareJD}
            className="bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-500 text-white px-8 py-3 rounded-2xl text-xs font-bold tracking-wide flex items-center justify-center gap-2 shadow-md dark:shadow-[0_0_15px_rgba(99,102,241,0.3)] transition-all active:scale-95"
          >
            <Send size={16} /> Share JD
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <SearchBar value={nameQuery} onChange={setNameQuery} placeholder="Search candidate name..." />
        <MultiSelectFilter values={vendorFilters} onChange={setVendorFilters} options={vendorOptions} icon={Building2} allLabel="All Vendors" />
        <SortToggle direction={sortDir} onToggle={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')} />
      </div>

      {rows.length === 0 ? (
        allRows.length === 0 ? (
          <EmptyState icon={Users} title="No Resumes Submitted Yet" subtitle="Resumes vendors submit for this JD will appear here." />
        ) : (
          <EmptyState icon={Users} title="No Matching Resumes" subtitle="No resumes match your filters. Try adjusting the search, vendor, or sort options." />
        )
      ) : (
        <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md rounded-[2rem] border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none overflow-x-auto">
          <table className="w-full text-left min-w-[1000px]">
            <thead className="bg-slate-50/50 dark:bg-white/5 text-[13px] text-slate-500 dark:text-slate-400 tracking-wide border-b border-slate-200 dark:border-white/10">
              <tr>
                <th className="px-6 py-5 font-semibold">Vendor Name</th>
                <th className="px-6 py-5 font-semibold">Candidate Name</th>
                <th className="px-6 py-5 font-semibold text-center">Exp</th>
                <th className="px-6 py-5 font-semibold text-center">Quoted Rate</th>
                <th className="px-6 py-5 font-semibold text-center">Rank</th>
                <th className="px-6 py-5 font-semibold text-center">Status</th>
                <th className="px-6 py-5 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-white/5">
              {rows.map((row, idx) => {
                const status = getStatus(row.id);
                const isInviting = invitingIds[row.id];

                return (
                  <tr key={row.id} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                    <td className="px-6 py-6 text-slate-600 dark:text-slate-300 text-sm">{row.vendorName}</td>
                    <td className="px-6 py-6 font-medium text-slate-900 dark:text-white text-sm">{row.candidateName}</td>
                    <td className="px-6 py-6 text-center text-slate-600 dark:text-slate-300 text-sm">{row.experience}</td>
                    <td className="px-6 py-6 text-center text-slate-900 dark:text-white font-semibold text-sm">{row.expectedCtc}</td>
                    <td className="px-6 py-6 text-center font-bold text-slate-500 dark:text-slate-400 text-sm">
                      #{idx + 1}{row.atsScore != null && <span className="text-[11px] text-slate-400 dark:text-slate-500 font-semibold"> ({row.atsScore})</span>}
                    </td>
                    <td className="px-6 py-6 text-center">
                      <span className={`px-2.5 py-1 rounded-md text-[12px] font-bold tracking-wide border ${RESUME_INVITE_STATUS_STYLES[status]}`}>
                        {status}
                      </span>
                    </td>
                    <td className="px-6 py-6 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setSelectedSubmission(row)}
                          className="p-2.5 bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-white/10 rounded-xl transition-all"
                          title="View Summary"
                        >
                          <FileText size={14} />
                        </button>
                        <button
                          onClick={() => setContactRow(row)}
                          className="p-2.5 bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-white/10 rounded-xl transition-all"
                          title="Contact Vendor"
                        >
                          <Mail size={14} />
                        </button>
                        <button
                          onClick={() => status === 'Not Sent' && handleSendInvitation(row.id)}
                          disabled={isInviting || status !== 'Not Sent'}
                          className={`px-4 py-2.5 rounded-xl text-[12px] font-bold tracking-wide flex items-center gap-2 transition-all border whitespace-nowrap ${
                            status !== 'Not Sent'
                              ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/20 cursor-default'
                              : 'bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-500 text-white border-transparent shadow-md dark:shadow-[0_0_15px_rgba(99,102,241,0.3)] active:scale-95 disabled:opacity-50'
                          }`}
                        >
                          {isInviting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                          {isInviting ? 'Sending...' : status === 'Not Sent' ? 'Send Invitation' : status}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selectedSubmission && (
        <SubmissionDetailModal submission={selectedSubmission} onClose={() => setSelectedSubmission(null)} />
      )}

      {contactRow && (
        <ContactVendorModal
          vendor={MOCK_VENDORS.find(v => v.id === contactRow.matchedVendorId)}
          candidateName={contactRow.candidateName}
          onClose={() => setContactRow(null)}
        />
      )}
    </div>
  );
};

// ------------------------------------------------------------
// RANK RESUMES — scoped to whichever JD is already open (via the
// "Rank Resumes" button inside JDResumesView). Scores every resume we
// have (any vendor, any original JD) against that same JD, no separate
// JD picker needed.
// ------------------------------------------------------------

// Deterministic mock score so the same candidate/JD pair always ranks
// the same way — once this is real, it's replaced by an actual ATS call.
const mockAtsScore = (resume, jd) => {
  const resumeSkills = resume.skills.toLowerCase().split(',').map(s => s.trim());
  const jdSkills = jd.skills.toLowerCase().split(',').map(s => s.trim());
  const overlap = resumeSkills.filter(s => jdSkills.includes(s)).length;
  const base = 55 + overlap * 12;
  const hash = (resume.id + jd.id).split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return Math.min(98, base + (hash % 9));
};

const RankResumesView = ({ jd, onBack }) => {
  const [inviteStatus, setInviteStatus] = useState({});
  const [invitingIds, setInvitingIds] = useState({});
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [contactRow, setContactRow] = useState(null);
  const [nameQuery, setNameQuery] = useState('');
  const [vendorFilters, setVendorFilters] = useState([]);
  const [sortDir, setSortDir] = useState('desc');

  const allRows = useMemo(() => {
    if (!jd) return [];
    return MOCK_RESUMES
      .map(resume => {
        const submission = MOCK_SUBMISSIONS.find(s => s.resumeId === resume.id);
        return {
          id: submission?.id || resume.id,
          resumeId: resume.id,
          candidateName: resume.candidateName,
          experience: resume.experience,
          vendorName: submission?.vendorName || '—',
          matchedVendorId: submission?.matchedVendorId || null,
          expectedCtc: submission?.expectedCtc || '—',
          atsScore: mockAtsScore(resume, jd),
          submission,
        };
      });
  }, [jd]);

  const vendorOptions = useMemo(() => (
    [...new Set(allRows.map(r => r.vendorName).filter(v => v !== '—'))].sort()
  ), [allRows]);

  const rows = useMemo(() => {
    const q = nameQuery.trim().toLowerCase();
    return allRows
      .filter(r => (!q || r.candidateName.toLowerCase().includes(q)))
      .filter(r => (vendorFilters.length === 0 || vendorFilters.includes(r.vendorName)))
      .sort((a, b) => sortDir === 'desc' ? b.atsScore - a.atsScore : a.atsScore - b.atsScore);
  }, [allRows, nameQuery, vendorFilters, sortDir]);

  const getStatus = (rowId) => inviteStatus[rowId] || 'Not Sent';

  // Reuses the existing campaign invite flow (POST /campaigns/, then
  // POST /campaigns/{id}/launch) already driven by Schedule.js.
  const handleSendInvitation = (rowId) => {
    setInvitingIds(prev => ({ ...prev, [rowId]: true }));
    setTimeout(() => {
      setInviteStatus(prev => ({ ...prev, [rowId]: 'Invite Sent' }));
      setInvitingIds(prev => ({ ...prev, [rowId]: false }));
    }, 500);
  };


  return (
    <div>
      <button
        onClick={onBack}
        className="flex items-center text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium mb-8 transition-colors w-max text-sm tracking-wide"
      >
        <ChevronLeft size={18} /> Back to Resumes
      </button>

      <h3 className="text-lg font-semibold tracking-wide text-slate-900 dark:text-white mb-1 flex items-center gap-2">
        <Trophy size={16} className="text-indigo-500 dark:text-indigo-400" /> Ranked Resumes
      </h3>
      <p className="text-[12px] text-slate-500 dark:text-slate-400 tracking-wide font-semibold mb-6">Every resume we have, scored against this JD.</p>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <SearchBar value={nameQuery} onChange={setNameQuery} placeholder="Search candidate name..." />
        <MultiSelectFilter values={vendorFilters} onChange={setVendorFilters} options={vendorOptions} icon={Building2} allLabel="All Vendors" />
        <SortToggle direction={sortDir} onToggle={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')} />
      </div>

      {rows.length === 0 ? (
        allRows.length === 0 ? (
          <EmptyState icon={Users} title="No Resumes Yet" subtitle="Resumes we've received will be ranked here." />
        ) : (
          <EmptyState icon={Users} title="No Matching Resumes" subtitle="No resumes match your filters. Try adjusting the search, vendor, or sort options." />
        )
      ) : (
        <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md rounded-[2rem] border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none overflow-x-auto">
          <table className="w-full text-left min-w-[1000px]">
            <thead className="bg-slate-50/50 dark:bg-white/5 text-[13px] text-slate-500 dark:text-slate-400 tracking-wide border-b border-slate-200 dark:border-white/10">
              <tr>
                <th className="px-6 py-5 font-semibold">Vendor Name</th>
                <th className="px-6 py-5 font-semibold">Candidate Name</th>
                <th className="px-6 py-5 font-semibold text-center">Exp</th>
                <th className="px-6 py-5 font-semibold text-center">Quoted Rate</th>
                <th className="px-6 py-5 font-semibold text-center">Rank</th>
                <th className="px-6 py-5 font-semibold text-center">Status</th>
                <th className="px-6 py-5 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-white/5">
              {rows.map((row, idx) => {
                const status = getStatus(row.id);
                const isInviting = invitingIds[row.id];

                return (
                  <tr key={row.id} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
                    <td className="px-6 py-6 text-slate-600 dark:text-slate-300 text-sm">{row.vendorName}</td>
                    <td className="px-6 py-6 font-medium text-slate-900 dark:text-white text-sm">{row.candidateName}</td>
                    <td className="px-6 py-6 text-center text-slate-600 dark:text-slate-300 text-sm">{row.experience}</td>
                    <td className="px-6 py-6 text-center text-slate-900 dark:text-white font-semibold text-sm">{row.expectedCtc}</td>
                    <td className="px-6 py-6 text-center">
                      <span className="inline-flex items-center gap-1 font-bold text-slate-500 dark:text-slate-400 text-sm">
                        #{idx + 1} <span className="text-[11px] text-slate-400 dark:text-slate-500 font-semibold">({row.atsScore})</span>
                      </span>
                    </td>
                    <td className="px-6 py-6 text-center">
                      <span className={`px-2.5 py-1 rounded-md text-[12px] font-bold tracking-wide border ${RESUME_INVITE_STATUS_STYLES[status]}`}>
                        {status}
                      </span>
                    </td>
                    <td className="px-6 py-6 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => row.submission && setSelectedSubmission(row.submission)}
                          disabled={!row.submission}
                          className="p-2.5 bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-white/10 rounded-xl transition-all disabled:opacity-40"
                          title="View Summary"
                        >
                          <FileText size={14} />
                        </button>
                        <button
                          onClick={() => setContactRow(row)}
                          disabled={!row.matchedVendorId}
                          className="p-2.5 bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-white/10 rounded-xl transition-all disabled:opacity-40"
                          title="Contact Vendor"
                        >
                          <Mail size={14} />
                        </button>
                        <button
                          onClick={() => status === 'Not Sent' && handleSendInvitation(row.id)}
                          disabled={isInviting || status !== 'Not Sent'}
                          className={`px-4 py-2.5 rounded-xl text-[12px] font-bold tracking-wide flex items-center gap-2 transition-all border whitespace-nowrap ${
                            status !== 'Not Sent'
                              ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/20 cursor-default'
                              : 'bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-500 text-white border-transparent shadow-md dark:shadow-[0_0_15px_rgba(99,102,241,0.3)] active:scale-95 disabled:opacity-50'
                          }`}
                        >
                          {isInviting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                          {isInviting ? 'Sending...' : status === 'Not Sent' ? 'Send Invitation' : status}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selectedSubmission && (
        <SubmissionDetailModal submission={selectedSubmission} onClose={() => setSelectedSubmission(null)} />
      )}

      {contactRow && (
        <ContactVendorModal
          vendor={MOCK_VENDORS.find(v => v.id === contactRow.matchedVendorId)}
          candidateName={contactRow.candidateName}
          onClose={() => setContactRow(null)}
        />
      )}
    </div>
  );
};

// ------------------------------------------------------------
// TAB: Job Requirement
// ------------------------------------------------------------
// Base path every JD drill-down route lives under.
const jdBasePath = (jdId) => `/vendor-sourcing/jd/${jdId}`;

const JDsTab = ({ query, onActiveJdChange }) => {
  const [jds, setJds] = useState(MOCK_JDS);
  const [showModal, setShowModal] = useState(false);
  const [staffingCompanyFilter, setStaffingCompanyFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { jdId } = useParams();

  const staffingCompanyOptions = useMemo(() => (
    [...new Set(jds.map(jd => jd.staffingCompany))].sort()
  ), [jds]);

  const filtered = useMemo(() => jds.filter(jd =>
    (jd.title.toLowerCase().includes(query.toLowerCase()) ||
      jd.skills.toLowerCase().includes(query.toLowerCase()) ||
      jd.staffingCompany.toLowerCase().includes(query.toLowerCase())) &&
    (!staffingCompanyFilter || jd.staffingCompany === staffingCompanyFilter) &&
    (!dateFrom || jd.createdAt >= dateFrom) &&
    (!dateTo || jd.createdAt <= dateTo)
  ), [jds, query, staffingCompanyFilter, dateFrom, dateTo]);

  const filtersActive = staffingCompanyFilter !== '' || dateFrom !== '' || dateTo !== '';

  // Which drill-down view (if any) the current URL points at, derived
  // purely from the path — not from local state — so back/forward and a
  // hard refresh both land on the right step.
  const view = useMemo(() => {
    if (!jdId) return 'list';
    if (location.pathname.endsWith('/assign')) return 'detail-assign';
    if (location.pathname.endsWith('/resumes')) return 'resumes';
    if (location.pathname.endsWith('/rank')) return 'rank';
    return 'detail-jd';
  }, [jdId, location.pathname]);

  const activeJd = jdId ? jds.find(j => j.id === jdId) : null;

  // Tell the parent which JD (if any) we're drilled into, so it can swap
  // the page-level header/stats/tabs for a focused single-JD view.
  useEffect(() => {
    onActiveJdChange?.(activeJd || null);
  }, [activeJd, onActiveJdChange]);

  // Stale/unknown jdId in the URL (e.g. a bad link) — bounce back to the
  // list instead of rendering a broken detail view.
  useEffect(() => {
    if (jdId && !activeJd) {
      navigate('/vendor-sourcing', { replace: true });
    }
  }, [jdId, activeJd, navigate]);

  // JDModal saves through the existing /jds/ API (AI generate, PDF upload,
  // or manual entry). Once posted here it's marked visible to vendors so
  // they can start marketing it to candidates.
  const handleJDCreated = (savedJd) => {
    setJds(prev => [{
      id: `jd-${savedJd.id}`,
      title: savedJd.title,
      skills: savedJd.skills,
      staffingCompany: 'Your Organization',
      openPositions: 1,
      createdAt: savedJd.created_at || new Date().toISOString(),
      visibleToVendors: true,
    }, ...prev]);
  };

  if (jdId) {
    if (!activeJd) {
      // Redirect is in flight (see effect above) — render nothing.
      return null;
    }

    if (view === 'detail-jd' || view === 'detail-assign') {
      return (
        <JDDetailView
          jd={activeJd}
          onBack={() => navigate(`${jdBasePath(jdId)}/resumes`)}
          onShared={() => navigate(`${jdBasePath(jdId)}/resumes`)}
          initialStage={view === 'detail-assign' ? 'assign' : 'jd'}
          onStageChange={(stage) => navigate(stage === 'assign' ? `${jdBasePath(jdId)}/assign` : jdBasePath(jdId))}
        />
      );
    }
    // Rank Resumes is scoped to the JD already open here — no separate
    // JD picker, it scores every resume we have against this same JD.
    if (view === 'rank') {
      return (
        <RankResumesView
          jd={activeJd}
          onBack={() => navigate(`${jdBasePath(jdId)}/resumes`)}
        />
      );
    }
    return (
      <JDResumesView
        jd={activeJd}
        onBack={() => navigate('/vendor-sourcing')}
        onShareJD={() => navigate(`${jdBasePath(jdId)}/assign`)}
        onRankResumes={() => navigate(`${jdBasePath(jdId)}/rank`)}
      />
    );
  }

  return (
    <>
      <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold tracking-wide text-slate-900 dark:text-white mb-1">Manage &amp; Distribute JDs</h2>
          <p className="text-[12px] text-slate-500 dark:text-slate-400 tracking-wide font-semibold">Create a JD, then open it to choose which vendors it's shared with.</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="shrink-0 bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-500 text-white px-8 py-3 rounded-2xl text-xs font-bold tracking-wide flex items-center justify-center gap-2 shadow-md dark:shadow-[0_0_15px_rgba(99,102,241,0.3)] transition-all active:scale-95 whitespace-nowrap"
        >
          <Plus size={16} strokeWidth={2} /> Create JD
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <FilterSelect value={staffingCompanyFilter} onChange={setStaffingCompanyFilter} options={staffingCompanyOptions} icon={Briefcase} allLabel="All Staffing Companies" />
        <DateRangeFilter from={dateFrom} to={dateTo} onFromChange={setDateFrom} onToChange={setDateTo} />
      </div>

      {filtered.length === 0 ? (
        jds.length === 0 ? (
          <EmptyState icon={FileText} title="No JDs Yet" subtitle="Create a JD to start sharing it with your vendors." />
        ) : (
          <EmptyState icon={FileText} title="No Matching JDs" subtitle={filtersActive || query ? "No JDs match your filters. Try adjusting the search, staffing company, or date range." : "No JDs Yet"} />
        )
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filtered.map((jd) => {
            // Scoped visibility: a JD is only marketable by vendors that
            // this JD's own staffing company created — never all vendors.
            const scopedVendors = MOCK_VENDORS.filter(v => v.staffingCompany === jd.staffingCompany);

            return (
              <div
                key={jd.id}
                onClick={() => navigate(`${jdBasePath(jd.id)}/resumes`)}
                className="bg-white dark:bg-white/5 backdrop-blur-md p-7 rounded-[2rem] border border-slate-200 dark:border-white/5 hover:border-slate-300 dark:hover:border-white/15 hover:bg-slate-50 dark:hover:bg-white/10 transition-all duration-300 group relative flex flex-col h-[280px] shadow-sm dark:shadow-none cursor-pointer"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-medium text-slate-900 dark:text-white text-xl pr-4 line-clamp-2 leading-snug">{jd.title}</h3>
                  <span className="shrink-0 px-2.5 py-1 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 text-[12px] rounded-md font-bold tracking-wide border border-indigo-200 dark:border-indigo-500/20 whitespace-nowrap">
                    {jd.openPositions} open
                  </span>
                </div>
                <p className="text-[12px] text-slate-500 dark:text-slate-400 mb-4 font-semibold tracking-wide flex items-center gap-1.5">
                  <Building2 size={12} /> {jd.staffingCompany}
                </p>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  {jd.skills.split(',').map((s, i) => (
                    <span key={i} className="px-2.5 py-1 bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-300 text-[12px] rounded-md font-bold tracking-wide border border-slate-200 dark:border-white/10 whitespace-nowrap">
                      {s.trim()}
                    </span>
                  ))}
                </div>

                <div className="mt-auto pt-4 border-t border-slate-100 dark:border-white/10">
                  <p className="text-[12px] text-slate-400 dark:text-slate-500 font-semibold tracking-wide mb-2 flex items-center gap-1.5">
                    <KeyRound size={12} /> {scopedVendors.length} vendor{scopedVendors.length !== 1 ? 's' : ''} eligible at {jd.staffingCompany}
                  </p>
                  {scopedVendors.length === 0 ? (
                    <p className="text-[12px] text-amber-600 dark:text-amber-400 font-semibold tracking-wide">No vendors created for this staffing company yet.</p>
                  ) : (
                    <p className="text-[12px] text-indigo-600 dark:text-indigo-400 font-bold tracking-wide flex items-center gap-1.5">
                      <Send size={12} /> Manage &amp; share with vendors
                    </p>
                  )}
                  <span className="text-[12px] text-slate-400 dark:text-slate-500 font-semibold tracking-wide block mt-3">Received: {new Date(jd.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <JDModal onClose={() => setShowModal(false)} onSuccess={handleJDCreated} />
      )}
    </>
  );
};

// ------------------------------------------------------------
// TAB: Resumes
// ------------------------------------------------------------
// Parses experience strings like "5 yrs" into a comparable number.
const parseExperienceYears = (exp) => parseFloat(exp) || 0;

const ResumesTab = ({ query }) => {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expSort, setExpSort] = useState(''); // '' | 'asc' | 'desc'

  const filtered = useMemo(() => {
    const rows = MOCK_RESUMES.filter(r =>
      (r.candidateName.toLowerCase().includes(query.toLowerCase()) ||
        r.skills.toLowerCase().includes(query.toLowerCase())) &&
      (!dateFrom || r.uploadedAt >= dateFrom) &&
      (!dateTo || r.uploadedAt <= dateTo)
    );
    if (expSort) {
      rows.sort((a, b) => expSort === 'asc'
        ? parseExperienceYears(a.experience) - parseExperienceYears(b.experience)
        : parseExperienceYears(b.experience) - parseExperienceYears(a.experience));
    }
    return rows;
  }, [query, dateFrom, dateTo, expSort]);

  const filtersActive = dateFrom !== '' || dateTo !== '';

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <DateRangeFilter from={dateFrom} to={dateTo} onFromChange={setDateFrom} onToChange={setDateTo} />
        <SortToggle
          direction={expSort === 'asc' ? 'asc' : 'desc'}
          onToggle={() => setExpSort(d => d === 'desc' ? 'asc' : 'desc')}
          label="Experience"
        />
      </div>

      {filtered.length === 0 ? (
        MOCK_RESUMES.length === 0 ? (
          <EmptyState icon={Users} title="No Resumes Found" subtitle="Resumes extracted from vendor submission mails will appear here." />
        ) : (
          <EmptyState icon={Users} title="No Matching Resumes" subtitle={filtersActive || query ? "No resumes match your filters. Try adjusting the search or date range." : "No Resumes Found"} />
        )
      ) : (
      <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md rounded-[2rem] border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none overflow-x-auto">
        <table className="w-full text-left min-w-[800px]">
          <thead className="bg-slate-50/50 dark:bg-white/5 text-[13px] text-slate-500 dark:text-slate-400 tracking-wide border-b border-slate-200 dark:border-white/10">
            <tr>
              <th className="px-6 py-5 font-semibold">Candidate</th>
              <th className="px-6 py-5 font-semibold">File</th>
              <th className="px-6 py-5 font-semibold">Skills</th>
              <th className="px-6 py-5 font-semibold text-center">Experience</th>
              <th className="px-6 py-5 font-semibold text-right">Uploaded</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-white/5">
            {filtered.map((r) => (
            <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
              <td className="px-6 py-6 font-medium text-slate-900 dark:text-white text-sm">{r.candidateName}</td>
              <td className="px-6 py-6 text-slate-500 dark:text-slate-400 text-[12px] font-mono">{r.fileName}</td>
              <td className="px-6 py-6">
                <div className="flex gap-2 flex-wrap">
                  {r.skills.split(',').map((s, i) => (
                    <span key={i} className="px-2.5 py-1 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 text-[12px] rounded-md font-bold tracking-wide border border-indigo-200 dark:border-indigo-500/20 whitespace-nowrap">
                      {s.trim()}
                    </span>
                  ))}
                </div>
              </td>
              <td className="px-6 py-6 text-center text-slate-600 dark:text-slate-300 text-sm font-semibold">{r.experience}</td>
              <td className="px-6 py-6 text-right text-[12px] text-slate-400 dark:text-slate-500 font-semibold tracking-wide">{new Date(r.uploadedAt).toLocaleDateString()}</td>
            </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </>
  );
};

// ------------------------------------------------------------
// TAB: Vendor Submissions
// ------------------------------------------------------------
const SubmissionDetailModal = ({ submission, onClose }) => {
  const rounds = submission?.rounds || [];
  const [activeRound, setActiveRound] = useState(rounds.length > 0 ? rounds[rounds.length - 1].round : 1);

  if (!submission) return null;
  const matchedVendor = MOCK_VENDORS.find(v => v.id === submission.matchedVendorId);
  const stageIdx = PIPELINE_STAGES.findIndex(s => s.key === submission.pipelineStage);
  const currentRound = rounds.find(r => r.round === activeRound);

  const hasAtsScore = submission.atsScore != null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex justify-center z-[60] px-4 pt-28 pb-10 overflow-y-auto">
      <div className="bg-[#0B0F19] rounded-[2rem] shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col relative border border-white/10 max-h-full self-start my-auto">
        <div className="px-10 pt-10 pb-6 flex justify-between items-start shrink-0 border-b border-white/5">
          <div className="min-w-0">
            <div className="flex items-start gap-8">
              <div>
                <p className="text-[12px] font-semibold text-slate-500 tracking-wide mb-1">Candidate Name</p>
                <h2 className="text-2xl font-semibold text-white tracking-wide">{submission.candidateName}</h2>
              </div>
              <div className="border-l border-white/5 pl-8">
                <p className="text-[12px] font-semibold text-slate-500 tracking-wide mb-1">Vendor Name</p>
                <h2 className="text-2xl font-semibold text-white tracking-wide">{submission.vendorName}</h2>
              </div>
            </div>
            {submission.jdTitle && (
              <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg">
                <FileText size={12} className="text-emerald-400 shrink-0" />
                <span className="text-[11px] font-bold tracking-wide text-slate-300">{submission.jdTitle}</span>
              </div>
            )}
          </div>
          <button onClick={onClose} className="w-12 h-12 flex items-center justify-center bg-white/5 rounded-2xl text-slate-400 hover:text-white hover:bg-white/10 transition-all border border-white/10 shrink-0">
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>

        <div className="px-10 py-8 space-y-8 overflow-y-auto custom-scrollbar min-h-0">
          {/* Stat row — the numbers a recruiter scans for first: ATS score
              (when the submission has one), candidate's CTC expectation,
              and contact number. Larger numerals, distinct from the flat
              card list below. */}
          <div className={`grid gap-4 ${hasAtsScore ? 'grid-cols-3' : 'grid-cols-2'}`}>
            {hasAtsScore && (
              <div className="bg-gradient-to-br from-amber-500/10 to-transparent rounded-2xl p-5 border border-amber-500/20">
                <p className="text-[11px] font-bold text-amber-300/80 tracking-wide uppercase mb-2 flex items-center gap-1.5"><Award size={13} /> ATS Score</p>
                <p className="text-3xl font-semibold text-white tracking-tight">{submission.atsScore}<span className="text-sm font-medium text-slate-500">/100</span></p>
              </div>
            )}
            <div className="bg-gradient-to-br from-emerald-500/10 to-transparent rounded-2xl p-5 border border-emerald-500/20">
              <p className="text-[11px] font-bold text-emerald-300/80 tracking-wide uppercase mb-2 flex items-center gap-1.5"><IndianRupee size={13} /> Candidate Expectation</p>
              <p className="text-3xl font-semibold text-white tracking-tight">{submission.expectedCtc}</p>
            </div>
            <div className="bg-gradient-to-br from-indigo-500/10 to-transparent rounded-2xl p-5 border border-indigo-500/20">
              <p className="text-[11px] font-bold text-indigo-300/80 tracking-wide uppercase mb-2 flex items-center gap-1.5"><Phone size={13} /> Contact No.</p>
              <p className="text-xl font-semibold text-white tracking-tight">{submission.candidatePhone || '—'}</p>
            </div>
          </div>

          {/* Stage timeline — JD Shared -> Resume Received -> Invite Sent ->
              Interview -> Waiting. Filled circles mark stages already passed.
              Given its own "progress element" treatment (section label,
              tighter spacing) rather than a generic card. */}
          <div>
            <p className="text-[11px] font-bold text-slate-500 tracking-widest uppercase mb-4">Pipeline Progress</p>
            <div className="flex items-center">
              {PIPELINE_STAGES.map((stage, i) => (
                <React.Fragment key={stage.key}>
                  <div className="flex flex-col items-center gap-2 shrink-0">
                    <div className={`w-4 h-4 rounded-full border-2 transition-colors ${
                      i <= stageIdx ? 'bg-indigo-500 border-indigo-500 shadow-[0_0_10px_rgba(99,102,241,0.6)]' : 'bg-transparent border-slate-600'
                    }`} />
                    <span className={`text-[11px] font-bold tracking-wide whitespace-nowrap ${i <= stageIdx ? 'text-white' : 'text-slate-500'}`}>
                      {stage.label}
                    </span>
                  </div>
                  {i < PIPELINE_STAGES.length - 1 && (
                    <div className={`flex-1 h-[2px] mx-1 mb-5 ${i < stageIdx ? 'bg-indigo-500' : 'bg-slate-800'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Interview rounds — tabs across Round 1 / 2 / 3, each with its
              own summary + suggestion. Given real visual weight since this
              is substantive content; the suggestion is called out with an
              accent border since it's actionable, unlike the summary. */}
          <div className="bg-black/30 rounded-2xl p-6 border border-white/10">
            <p className="text-sm font-semibold text-white tracking-wide mb-5 flex items-center gap-2">
              <Users size={16} className="text-indigo-400" /> Interview Rounds
            </p>

            {rounds.length === 0 ? (
              <p className="text-slate-500 text-[12px] italic">No interview rounds yet.</p>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-5">
                  {rounds.map((r) => (
                    <button
                      key={r.round}
                      onClick={() => setActiveRound(r.round)}
                      className={`px-4 py-2 rounded-xl text-[12px] font-bold tracking-wide transition-all border ${
                        activeRound === r.round
                          ? 'bg-indigo-600 text-white border-transparent shadow-[0_0_15px_rgba(99,102,241,0.3)]'
                          : 'bg-black/30 text-slate-400 border-white/10 hover:border-white/20 hover:text-slate-200'
                      }`}
                    >
                      Round {r.round}
                    </button>
                  ))}
                </div>

                {currentRound && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-white text-sm font-semibold tracking-wide">{currentRound.title}</p>
                      <span className={`px-2.5 py-1 rounded-md text-[12px] font-bold tracking-wide border ${ROUND_STATUS_STYLES[currentRound.status]}`}>
                        {currentRound.status}
                      </span>
                    </div>
                    <div className="bg-black/40 rounded-xl p-4 border border-white/5">
                      <p className="text-[11px] font-bold text-slate-500 tracking-widest uppercase mb-2">Interview Summary</p>
                      <p className="text-slate-300 text-[13px] leading-[1.7]">{currentRound.summary}</p>
                    </div>
                    <div className="bg-indigo-500/[0.07] rounded-xl p-4 border-l-2 border-indigo-400 border-y border-r border-y-white/5 border-r-white/5">
                      <p className="text-[11px] font-bold text-indigo-300 tracking-widest uppercase mb-2 flex items-center gap-1.5">
                        <MessageSquareText size={12} /> Suggestion
                      </p>
                      <p className="text-slate-200 text-[13px] leading-[1.7]">{currentRound.suggestion}</p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Secondary detail cards — vendor/staffing company are already
              shown in the header above, so only JD-adjacent detail (resume
              file) and the less-critical marketed CTC live here. */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-black/30 rounded-xl p-4 border border-white/5">
              <p className="text-[11px] font-semibold text-slate-500 tracking-wide mb-1.5 flex items-center gap-1.5"><Users size={12} className="text-amber-400" /> Resume File</p>
              <p className="text-white text-sm font-medium truncate">{MOCK_RESUMES.find(r => r.id === submission.resumeId)?.fileName || '—'}</p>
            </div>
            <div className="bg-black/30 rounded-xl p-4 border border-white/5">
              <p className="text-[11px] font-semibold text-slate-500 tracking-wide mb-1.5 flex items-center gap-1.5"><IndianRupee size={12} className="text-slate-400" /> Marketed CTC</p>
              <p className="text-white text-sm font-medium">{submission.marketedCtc}</p>
            </div>
          </div>

          {/* Auto-detection pipeline — vendor mails the staffing company,
              staffing company's inbox auto-forwards to our ingestion
              mailbox, sender is matched against a registered vendor login,
              the mail is classified, and fields are lifted from it.
              Useful provenance/audit info, but not what a recruiter scans
              for first — kept at the bottom with a quieter, more compact
              treatment than the sections above. */}
          <div className="bg-black/20 rounded-2xl p-4 border border-white/5">
            <p className="text-[10px] font-semibold text-slate-500 tracking-wide mb-3 flex items-center gap-1.5"><Mail size={11} className="text-violet-400/70" /> Auto-Detected from Vendor Mail</p>

            {/* Hop chain: Vendor -> Staffing Co. Inbox -> Forwarded -> Detected */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-1.5 mb-3">
              {[
                { label: submission.vendorName, sub: submission.senderEmail },
                { label: submission.staffingCompany, sub: 'Original recipient' },
                { label: submission.forwardedToInbox, sub: 'Forwarded to (ours)' },
              ].map((hop, i, arr) => (
                <React.Fragment key={i}>
                  <div className="flex-1 min-w-0 bg-black/30 border border-white/5 rounded-lg px-3 py-2">
                    <p className="text-slate-300 text-[11px] font-semibold tracking-wide truncate" title={hop.label}>{hop.label}</p>
                    <p className="text-slate-600 text-[10px] tracking-wide truncate">{hop.sub}</p>
                  </div>
                  {i < arr.length - 1 && (
                    <ChevronRight size={13} className="text-slate-700 shrink-0 mx-auto sm:mx-0 rotate-90 sm:rotate-0" />
                  )}
                </React.Fragment>
              ))}
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between bg-black/20 rounded-lg px-3 py-2 border border-white/5">
                <span className="text-[11px] text-slate-600 flex items-center gap-1.5"><KeyRound size={11} /> Sender matched vendor login</span>
                <span className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-400/80">
                  <ShieldCheck size={12} />
                  {submission.senderEmail} {matchedVendor && `(${matchedVendor.companyName})`}
                </span>
              </div>
              <div className="flex items-center justify-between bg-black/20 rounded-lg px-3 py-2 border border-white/5">
                <span className="text-[11px] text-slate-600 flex items-center gap-1.5"><Sparkles size={11} /> Mail classification</span>
                <span className="text-[11px] font-semibold text-indigo-300/80">{submission.classification || 'Candidate Submission'}</span>
              </div>
              <div className="flex items-center justify-between bg-black/20 rounded-lg px-3 py-2 border border-white/5">
                <span className="text-[11px] text-slate-600">Fields extracted</span>
                <span className="text-[11px] font-semibold text-slate-400">Resume, JD, CTC, Contact No.</span>
              </div>
            </div>

            <div className="mt-3 pt-3 border-t border-white/5">
              <p className="text-slate-400 text-[12px] font-medium mb-0.5">{submission.mailSubject}</p>
              <p className="text-slate-600 text-[11px]">{submission.mailReceivedAt}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const STATUS_OPTIONS = ['New', 'Reviewed', 'Shortlisted'];

// mailReceivedAt looks like '2026-09-12 10:14 AM' — compare just the date
// portion (first 10 chars, YYYY-MM-DD) against the plain <input type="date">
// values so no timezone-sensitive Date parsing is needed.
const mailDatePart = (mailReceivedAt) => mailReceivedAt.slice(0, 10);

const SubmissionsTab = ({ query }) => {
  const [selected, setSelected] = useState(null);
  const [vendorFilters, setVendorFilters] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const vendorOptions = useMemo(() => (
    [...new Set(MOCK_SUBMISSIONS.map(s => s.vendorName))].sort()
  ), []);

  const filtered = useMemo(() => MOCK_SUBMISSIONS.filter(s => {
    const datePart = mailDatePart(s.mailReceivedAt);
    return (
      (s.candidateName.toLowerCase().includes(query.toLowerCase()) ||
        s.vendorName.toLowerCase().includes(query.toLowerCase()) ||
        s.staffingCompany.toLowerCase().includes(query.toLowerCase()) ||
        s.jdTitle.toLowerCase().includes(query.toLowerCase())) &&
      (vendorFilters.length === 0 || vendorFilters.includes(s.vendorName)) &&
      (!statusFilter || s.status === statusFilter) &&
      (!dateFrom || datePart >= dateFrom) &&
      (!dateTo || datePart <= dateTo)
    );
  }), [query, vendorFilters, statusFilter, dateFrom, dateTo]);

  const filtersActive = vendorFilters.length > 0 || statusFilter !== '' || dateFrom !== '' || dateTo !== '';

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <MultiSelectFilter values={vendorFilters} onChange={setVendorFilters} options={vendorOptions} icon={Building2} allLabel="All Vendors" />
        <FilterSelect value={statusFilter} onChange={setStatusFilter} options={STATUS_OPTIONS} icon={Filter} allLabel="All Statuses" />
        <DateRangeFilter from={dateFrom} to={dateTo} onFromChange={setDateFrom} onToChange={setDateTo} />
      </div>

      {filtered.length === 0 ? (
        MOCK_SUBMISSIONS.length === 0 ? (
          <EmptyState icon={Building2} title="No Vendor Submissions Found" subtitle="Candidate submissions parsed from vendor mails will appear here." />
        ) : (
          <EmptyState icon={Building2} title="No Matching Submissions" subtitle={filtersActive || query ? "No submissions match your filters. Try adjusting the search, vendor, status, or date range." : "No Vendor Submissions Found"} />
        )
      ) : (
      <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md rounded-[2rem] border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none overflow-x-auto">
        <table className="w-full text-left min-w-[1000px]">
          <thead className="bg-slate-50/50 dark:bg-white/5 text-[13px] text-slate-500 dark:text-slate-400 tracking-wide border-b border-slate-200 dark:border-white/10">
            <tr>
              <th className="px-6 py-5 font-semibold">Candidate</th>
              <th className="px-6 py-5 font-semibold">Vendor</th>
              <th className="px-6 py-5 font-semibold">Staffing Co.</th>
              <th className="px-6 py-5 font-semibold">JD</th>
              <th className="px-6 py-5 font-semibold text-center">Expected CTC</th>
              <th className="px-6 py-5 font-semibold text-center">Status</th>
              <th className="px-6 py-5 font-semibold text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-white/5">
            {filtered.map((s) => (
              <tr key={s.id} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors group">
                <td className="px-6 py-6">
                  <p className="font-medium text-slate-900 dark:text-white text-sm mb-1">{s.candidateName}</p>
                  <p className="text-[12px] text-slate-500 dark:text-slate-400 font-mono mb-1.5">{s.candidateEmail}</p>
                  {s.senderEmail && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 text-[10px] font-bold tracking-wide rounded-md border border-emerald-200 dark:border-emerald-500/20">
                      <ShieldCheck size={10} /> Auto-detected
                    </span>
                  )}
                </td>
                <td className="px-6 py-6 text-slate-600 dark:text-slate-300 text-sm">{s.vendorName}</td>
                <td className="px-6 py-6 text-slate-600 dark:text-slate-300 text-sm">{s.staffingCompany}</td>
                <td className="px-6 py-6 text-slate-600 dark:text-slate-300 text-sm">{s.jdTitle}</td>
                <td className="px-6 py-6 text-center text-slate-900 dark:text-white font-semibold text-sm">{s.expectedCtc}</td>
                <td className="px-6 py-6 text-center">
                  <span className={`px-2.5 py-1 rounded-md text-[12px] font-bold tracking-wide border ${STATUS_STYLES[s.status]}`}>
                    {s.status}
                  </span>
                </td>
                <td className="px-6 py-6 text-right">
                  <button
                    onClick={() => setSelected(s)}
                    className="bg-slate-100 dark:bg-white/5 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-600 hover:text-white dark:hover:bg-indigo-500 border border-transparent dark:border-white/10 px-5 py-2.5 rounded-xl text-xs font-bold tracking-wide flex items-center justify-end gap-2 ml-auto transition-all shadow-sm dark:shadow-none"
                  >
                    View <ChevronRight size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
      {selected && <SubmissionDetailModal submission={selected} onClose={() => setSelected(null)} />}
    </>
  );
};


// ------------------------------------------------------------
// TAB: Vendor Accounts (staffing company creates vendor logins)
// ------------------------------------------------------------
const VendorAccountModal = ({ onClose, onCreate }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    password: '',
    phone: '',
    staffingCompany: '',
    accessLevel: 'Standard',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    // Mock create — once the vendor portal + backend endpoint exist, this
    // becomes api.post('/vendors/', formData) returning the created account.
    setTimeout(() => {
      onCreate({
        id: `ven-${Date.now()}`,
        ...formData,
        status: 'Pending',
        createdAt: new Date().toISOString().slice(0, 10),
      });
      setIsLoading(false);
      onClose();
    }, 500);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex justify-center z-[60] px-4 pt-28 pb-10 overflow-y-auto">
      <div className="bg-[#0B0F19] rounded-[2rem] shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col relative border border-white/10 max-h-full self-start my-auto">
        <div className="px-10 pt-10 pb-6 flex justify-between items-start shrink-0 border-b border-white/5">
          <div>
            <h2 className="text-3xl font-semibold text-white tracking-wide mb-1">Create Vendor Account</h2>
            <p className="text-slate-500 text-[12px] tracking-wide font-semibold">Grant a vendor login access to submit candidates</p>
          </div>
          <button onClick={onClose} className="w-12 h-12 flex items-center justify-center bg-white/5 rounded-2xl text-slate-400 hover:text-white hover:bg-white/10 transition-all border border-white/10">
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 px-10 pt-6 pb-10 overflow-y-auto custom-scrollbar">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-[12px] font-semibold text-slate-500 tracking-wide mb-3 flex items-center gap-2"><Building2 size={14} className="text-indigo-400" /> Vendor Company Name</label>
                <input required value={formData.companyName} onChange={e => setFormData({ ...formData, companyName: e.target.value })} className="w-full bg-black/50 border border-white/10 rounded-2xl px-5 py-4 text-white font-medium text-sm placeholder:text-[12px] focus:outline-none focus:border-indigo-500 transition-all placeholder-slate-600" placeholder="e.g. TalentBridge Solutions" />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-slate-500 tracking-wide mb-3 flex items-center gap-2"><Users size={14} className="text-indigo-400" /> Contact Person</label>
                <input required value={formData.contactName} onChange={e => setFormData({ ...formData, contactName: e.target.value })} className="w-full bg-black/50 border border-white/10 rounded-2xl px-5 py-4 text-white font-medium text-sm placeholder:text-[12px] focus:outline-none focus:border-indigo-500 transition-all placeholder-slate-600" placeholder="e.g. Priya Nair" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-[12px] font-semibold text-slate-500 tracking-wide mb-3 flex items-center gap-2"><Mail size={14} className="text-violet-400" /> Login Email</label>
                <input required type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="w-full bg-black/50 border border-white/10 rounded-2xl px-5 py-4 text-white font-medium text-sm placeholder:text-[12px] focus:outline-none focus:border-indigo-500 transition-all placeholder-slate-600" placeholder="vendor@company.com" />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-slate-500 tracking-wide mb-3 flex items-center gap-2"><Lock size={14} className="text-violet-400" /> Temporary Password</label>
                <input required type="password" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} className="w-full bg-black/50 border border-white/10 rounded-2xl px-5 py-4 text-white font-medium text-sm placeholder:text-[12px] focus:outline-none focus:border-indigo-500 transition-all placeholder-slate-600" placeholder="••••••••" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-[12px] font-semibold text-slate-500 tracking-wide mb-3 flex items-center gap-2"><Phone size={14} className="text-emerald-400" /> Phone</label>
                <input value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} className="w-full bg-black/50 border border-white/10 rounded-2xl px-5 py-4 text-white font-medium text-sm placeholder:text-[12px] focus:outline-none focus:border-indigo-500 transition-all placeholder-slate-600" placeholder="+91 98765 43210" />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-slate-500 tracking-wide mb-3 flex items-center gap-2"><Briefcase size={14} className="text-emerald-400" /> Assigned Staffing Company</label>
                <input required value={formData.staffingCompany} onChange={e => setFormData({ ...formData, staffingCompany: e.target.value })} className="w-full bg-black/50 border border-white/10 rounded-2xl px-5 py-4 text-white font-medium text-sm placeholder:text-[12px] focus:outline-none focus:border-indigo-500 transition-all placeholder-slate-600" placeholder="e.g. ProStaff Inc" />
              </div>
            </div>

            <div>
              <label className="block text-[12px] font-semibold text-slate-500 tracking-wide mb-3 flex items-center gap-2"><ShieldCheck size={14} className="text-amber-400" /> Access Level</label>
              <div className="grid grid-cols-3 gap-3">
                {ACCESS_LEVELS.map((level) => (
                  <button
                    key={level}
                    type="button"
                    onClick={() => setFormData({ ...formData, accessLevel: level })}
                    className={`px-4 py-3.5 rounded-2xl text-xs font-bold tracking-wide transition-all border ${
                      formData.accessLevel === level
                        ? 'bg-indigo-500/10 text-indigo-300 border-indigo-500/50'
                        : 'bg-black/30 text-slate-500 border-white/10 hover:border-white/20 hover:text-slate-300'
                    }`}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-8 shrink-0 border-t border-white/5 mt-8">
            <button type="submit" disabled={isLoading} className="bg-indigo-600 hover:bg-indigo-500 text-white px-10 py-3.5 rounded-2xl text-xs font-bold tracking-wide transition-all shadow-[0_0_15px_rgba(99,102,241,0.4)] active:scale-95 flex items-center gap-3 disabled:opacity-50">
              {isLoading && <Loader2 className="animate-spin" size={16} />}
              Create Vendor Account
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const VENDOR_STATUS_OPTIONS = ['Active', 'Pending', 'Suspended'];

const VendorAccountsTab = ({ query }) => {
  const [vendors, setVendors] = useState(MOCK_VENDORS);
  const [showModal, setShowModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const filtered = useMemo(() => vendors.filter(v =>
    (v.companyName.toLowerCase().includes(query.toLowerCase()) ||
      v.contactName.toLowerCase().includes(query.toLowerCase()) ||
      v.staffingCompany.toLowerCase().includes(query.toLowerCase())) &&
    (!statusFilter || v.status === statusFilter) &&
    (!dateFrom || v.createdAt >= dateFrom) &&
    (!dateTo || v.createdAt <= dateTo)
  ), [vendors, query, statusFilter, dateFrom, dateTo]);

  const filtersActive = statusFilter !== '' || dateFrom !== '' || dateTo !== '';

  const [copied, setCopied] = useState(false);
  const ingestionInbox = 'ingest@recruitguru.ai';

  const handleCopyInbox = () => {
    navigator.clipboard?.writeText(ingestionInbox);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <>
      {/* Forwarding instructions — the vendor mails the staffing company,
          and the staffing company sets up forwarding to this inbox so we
          can detect + extract the submission. */}
      <div className="bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/20 rounded-2xl px-6 py-5 mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <Mail size={18} className="text-indigo-600 dark:text-indigo-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-indigo-900 dark:text-white">Give staffing companies this inbox to forward vendor mails to</p>
            <p className="text-[12px] text-indigo-700/80 dark:text-indigo-300/80 tracking-wide mt-1">Vendor sends candidate mail to the staffing company &rarr; staffing company forwards it here &rarr; we detect and extract it automatically.</p>
          </div>
        </div>
        <button
          onClick={handleCopyInbox}
          className="shrink-0 bg-white dark:bg-black/40 border border-indigo-200 dark:border-indigo-500/30 text-indigo-700 dark:text-indigo-300 px-5 py-3 rounded-xl text-xs font-bold tracking-wide font-mono flex items-center gap-2 hover:bg-indigo-100 dark:hover:bg-white/5 transition-all"
        >
          {copied ? <ShieldCheck size={14} className="text-emerald-500" /> : <Mail size={14} />}
          {copied ? 'Copied!' : ingestionInbox}
        </button>
      </div>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <FilterSelect value={statusFilter} onChange={setStatusFilter} options={VENDOR_STATUS_OPTIONS} icon={Filter} allLabel="All Statuses" />
          <DateRangeFilter from={dateFrom} to={dateTo} onFromChange={setDateFrom} onToChange={setDateTo} />
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="shrink-0 bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-500 text-white px-8 py-3 rounded-2xl text-xs font-bold tracking-wide flex items-center justify-center gap-2 shadow-md dark:shadow-[0_0_15px_rgba(99,102,241,0.3)] transition-all active:scale-95 whitespace-nowrap"
        >
          <Plus size={16} strokeWidth={2} /> Create Vendor Account
        </button>
      </div>

      {filtered.length === 0 ? (
        vendors.length === 0 ? (
          <EmptyState icon={KeyRound} title="No Vendor Accounts Found" subtitle="Create a vendor account to grant them access to submit candidates." />
        ) : (
          <EmptyState icon={KeyRound} title="No Matching Vendor Accounts" subtitle={filtersActive || query ? "No vendor accounts match your filters. Try adjusting the search, status, or date range." : "No Vendor Accounts Found"} />
        )
      ) : (
        <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md rounded-[2rem] border border-slate-200 dark:border-white/10 shadow-sm dark:shadow-none overflow-x-auto">
          <table className="w-full text-left min-w-[900px]">
            <thead className="bg-slate-50/50 dark:bg-white/5 text-[13px] text-slate-500 dark:text-slate-400 tracking-wide border-b border-slate-200 dark:border-white/10">
              <tr>
                <th className="px-6 py-5 font-semibold">Vendor</th>
                <th className="px-6 py-5 font-semibold">Login Email</th>
                <th className="px-6 py-5 font-semibold">Staffing Co.</th>
                <th className="px-6 py-5 font-semibold text-center">Access</th>
                <th className="px-6 py-5 font-semibold text-center">Status</th>
                <th className="px-6 py-5 font-semibold text-right">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-white/5">
              {filtered.map((v) => (
                <tr key={v.id} className="hover:bg-slate-50 dark:hover:bg-white/5 transition-colors group">
                  <td className="px-6 py-6">
                    <p className="font-medium text-slate-900 dark:text-white text-sm mb-1">{v.companyName}</p>
                    <p className="text-[12px] text-slate-500 dark:text-slate-400 tracking-wide">{v.contactName}</p>
                  </td>
                  <td className="px-6 py-6 text-slate-600 dark:text-slate-300 text-[12px] font-mono">{v.email}</td>
                  <td className="px-6 py-6 text-slate-600 dark:text-slate-300 text-sm">{v.staffingCompany}</td>
                  <td className="px-6 py-6 text-center">
                    <span className="px-2.5 py-1 bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-300 text-[12px] rounded-md font-bold tracking-wide border border-slate-200 dark:border-white/10 whitespace-nowrap">
                      {v.accessLevel}
                    </span>
                  </td>
                  <td className="px-6 py-6 text-center">
                    <span className={`px-2.5 py-1 rounded-md text-[12px] font-bold tracking-wide border ${VENDOR_STATUS_STYLES[v.status]}`}>
                      {v.status}
                    </span>
                  </td>
                  <td className="px-6 py-6 text-right text-[12px] text-slate-400 dark:text-slate-500 font-semibold tracking-wide">{new Date(v.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <VendorAccountModal
          onClose={() => setShowModal(false)}
          onCreate={(vendor) => setVendors(prev => [vendor, ...prev])}
        />
      )}
    </>
  );
};

// ------------------------------------------------------------
// MAIN PAGE
// ------------------------------------------------------------
const VendorSourcing = () => {
  const [activeTab, setActiveTab] = useState('jds');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeJd, setActiveJd] = useState(null);
  const scrollRef = React.useRef(null);
  const { pathname } = useLocation();

  // Client-side route changes (JD -> Assign Vendors -> Resumes -> Rank)
  // don't reset scroll like a full page load would, so each step can open
  // mid-scroll from wherever the previous step left off. The window is the
  // actual scroll container (DashboardLayout's <main> has no overflow of
  // its own), but reset this inner container too in case that ever changes.
  useEffect(() => {
    window.scrollTo(0, 0);
    scrollRef.current?.scrollTo(0, 0);
  }, [pathname]);

  const searchPlaceholders = {
    jds: 'Search JDs, skills, staffing company...',
    resumes: 'Search resumes by candidate or skill...',
    submissions: 'Search candidate, vendor, staffing co...',
    vendors: 'Search vendor, contact, staffing co...',
  };

  return (
    <div className="flex-1 min-h-screen bg-slate-50 dark:bg-[#05050A] text-slate-900 dark:text-slate-200 font-sans flex flex-col relative overflow-hidden transition-colors duration-500">
      <style>{customStyles}</style>
      <AmbientGlows />

      <div ref={scrollRef} className={`flex-1 overflow-y-auto px-6 lg:px-12 w-full max-w-[1600px] mx-auto custom-scrollbar relative z-10 ${activeJd ? 'py-8 lg:py-10' : 'py-12 lg:py-20'}`}>

        {activeJd ? (
          /* --- FOCUSED SINGLE-JD HEADER --- */
          <div className="mb-6">
            <h1 className="text-3xl font-semibold tracking-wide text-slate-900 dark:text-white mb-1 flex items-center gap-3">
              <FileText size={22} className="text-indigo-500 dark:text-indigo-400" />
              {activeJd.title}
            </h1>
            <p className="text-[12px] text-slate-500 dark:text-slate-400 tracking-wide font-semibold flex items-center gap-1.5">
              <Building2 size={12} /> {activeJd.staffingCompany}
            </p>
          </div>
        ) : (
          <>
            {/* --- HEADER --- */}
            <div className="mb-10">
              <h1 className="text-3xl font-semibold tracking-wide text-slate-900 dark:text-white mb-1 flex items-center gap-3">
                <Sparkles size={22} className="text-indigo-500 dark:text-indigo-400" />
                Vendor Sourcing
              </h1>
              <p className="text-[12px] text-slate-500 dark:text-slate-400 tracking-wide font-semibold">
                Staffing companies &rarr; vendors &rarr; candidates &rarr; submission mail &rarr; ATS ranking, all in one place.
              </p>
            </div>

            {/* --- STATS --- */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-slate-200 dark:bg-white/10 border border-slate-200 dark:border-white/10 rounded-[2rem] overflow-hidden mb-12">
              <StatCard icon={FileText} label="Vendor JDs" value={MOCK_JDS.length} accent="text-indigo-600 dark:text-indigo-400" />
              <StatCard icon={Users} label="Resumes Received" value={MOCK_RESUMES.length} accent="text-violet-600 dark:text-violet-400" />
              <StatCard icon={Building2} label="Vendor Submissions" value={MOCK_SUBMISSIONS.length} accent="text-emerald-600 dark:text-emerald-400" />
              <StatCard icon={Trophy} label="Shortlisted" value={MOCK_SUBMISSIONS.filter(s => s.status === 'Shortlisted').length} accent="text-amber-600 dark:text-amber-400" />
            </div>

            {/* --- TABS + SEARCH --- */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-10 border-b border-slate-200 dark:border-white/10 pb-6">
              <div className="flex items-center gap-2 bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-2xl p-1.5 shadow-sm dark:shadow-none overflow-x-auto">
                {TABS.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => { setActiveTab(tab.id); setSearchQuery(''); }}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold tracking-wide whitespace-nowrap transition-all duration-300 ${
                        isActive
                          ? 'bg-indigo-600 text-white shadow-[0_0_15px_rgba(99,102,241,0.3)]'
                          : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5'
                      }`}
                    >
                      <Icon size={15} />
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder={searchPlaceholders[activeTab]} />
            </div>
          </>
        )}

        {/* --- TAB CONTENT --- */}
        {activeTab === 'jds' && <JDsTab query={searchQuery} onActiveJdChange={setActiveJd} />}
        {activeTab === 'resumes' && <ResumesTab query={searchQuery} />}
        {activeTab === 'submissions' && <SubmissionsTab query={searchQuery} />}
        {activeTab === 'vendors' && <VendorAccountsTab query={searchQuery} />}
      </div>
    </div>
  );
};

export default VendorSourcing;
